import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { api, apiErr } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { ShoppingBag, Plus, Minus, Loader2, MapPin, Phone, Trash2, UtensilsCrossed } from "lucide-react";

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

// Pick readable text color for a given HSL string based on its lightness.
function contrastFg(hsl) {
  try {
    const l = Number(hsl.split(/\s+/)[2].replace("%", ""));
    return l > 62 ? "0 0% 12%" : "0 0% 100%";
  } catch { return "0 0% 100%"; }
}

export default function Storefront() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [cart, setCart] = useState([]);
  const [activeCat, setActiveCat] = useState("");
  const [modItem, setModItem] = useState(null);
  const [selMods, setSelMods] = useState({});
  const [selIngr, setSelIngr] = useState({});
  const [cartOpen, setCartOpen] = useState(false);
  const [customer, setCustomer] = useState({ name: "", phone: "", address: "" });
  const [zoneError, setZoneError] = useState("");
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    api.get(`/restaurants/${slug}/menu`)
      .then(({ data }) => { setData(data); if (data.categories[0]) setActiveCat(data.categories[0].id); })
      .catch((e) => setError(apiErr(e)));
  }, [slug]);

  // Real-time open/closed status: poll every 6s and reflect changes live.
  useEffect(() => {
    const tick = () => api.get(`/restaurants/${slug}/status`)
      .then(({ data: s }) => setData((prev) => (prev && prev.restaurant.is_open !== s.is_open
        ? { ...prev, restaurant: { ...prev.restaurant, is_open: s.is_open } } : prev)))
      .catch(() => {});
    const id = setInterval(tick, 6000);
    return () => clearInterval(id);
  }, [slug]);

  const theme = data?.restaurant?.theme;
  const themeStyle = useMemo(() => theme ? {
    "--primary": theme.primary_color,
    "--primary-foreground": contrastFg(theme.primary_color),
    "--accent": theme.accent_color,
    "--accent-foreground": contrastFg(theme.accent_color),
    "--background": theme.background_color,
    "--ring": theme.primary_color,
    "--font-heading": `'${theme.font_heading}'`,
    "--font-body": `'${theme.font_body}'`,
  } : {}, [theme]);

  const addToCart = (item, mods = [], removed = []) => {
    const modIds = mods.map((m) => m.id).sort();
    const key = item.id + "|" + modIds.join(",") + "|" + removed.slice().sort().join(",");
    const unit = item.price + mods.reduce((s, m) => s + m.price_delta, 0);
    setCart((prev) => {
      const ex = prev.find((c) => c.key === key);
      if (ex) return prev.map((c) => c.key === key ? { ...c, quantity: c.quantity + 1 } : c);
      return [...prev, { key, menu_item_id: item.id, name: item.name, image: item.image_url, unit, modifiers: mods, removed, quantity: 1 }];
    });
    toast.success(`${item.name} añadido`);
  };

  const onAdd = (item) => {
    if (!data?.restaurant?.is_open) { toast.error("El restaurante está cerrado ahora mismo"); return; }
    const hasMods = item.modifier_groups && item.modifier_groups.length > 0;
    const hasIngr = item.ingredients && item.ingredients.length > 0;
    if (hasMods || hasIngr) {
      setModItem(item);
      setSelMods({});
      setSelIngr(Object.fromEntries((item.ingredients || []).map((ing) => [ing, true])));
    } else addToCart(item);
  };

  const confirmMods = () => {
    const chosen = [];
    (modItem.modifier_groups || []).forEach((g) => g.modifiers.forEach((m) => { if (selMods[m.id]) chosen.push(m); }));
    const removed = (modItem.ingredients || []).filter((ing) => !selIngr[ing]);
    addToCart(modItem, chosen, removed);
    setModItem(null);
  };

  const setQty = (key, delta) => setCart((prev) =>
    prev.map((c) => c.key === key ? { ...c, quantity: Math.max(0, c.quantity + delta) } : c).filter((c) => c.quantity > 0));

  const subtotal = cart.reduce((s, c) => s + c.unit * c.quantity, 0);

  const checkout = async () => {
    if (cart.length === 0) return;
    if (closed) { toast.error("El restaurante está cerrado ahora mismo"); return; }
    setZoneError("");
    setPaying(true);
    try {
      if (hasZones) {
        const { data: chk } = await api.post(`/restaurants/${slug}/delivery-check`, { address: customer.address });
        if (!chk.available) { setZoneError(chk.message); setPaying(false); return; }
      }
      const items = cart.map((c) => ({ menu_item_id: c.menu_item_id, quantity: c.quantity, modifier_ids: c.modifiers.map((m) => m.id), removed_ingredients: c.removed || [] }));
      const { data: res } = await api.post("/payments/checkout", {
        restaurant_slug: slug, items, customer_name: customer.name, customer_phone: customer.phone,
        customer_address: customer.address, origin_url: window.location.origin,
      });
      window.location.href = res.checkout_url;
    } catch (e) {
      toast.error(apiErr(e));
      setPaying(false);
    }
  };

  if (error) return <div className="min-h-screen flex items-center justify-center text-neutral-500">{error}</div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center bg-neutral-50"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  const { restaurant, categories } = data;
  const count = cart.reduce((s, c) => s + c.quantity, 0);
  const closed = !restaurant.is_open;
  const deliveryFee = Number(restaurant.delivery_fee || 0);
  const grandTotal = subtotal + deliveryFee;
  const zones = restaurant.delivery_zones || [];
  const hasZones = zones.length > 0;

  return (
    <div style={themeStyle} className="min-h-screen font-body bg-background text-neutral-900">
      {/* Hero */}
      <div className="relative h-[46vh] min-h-[300px] w-full overflow-hidden">
        <img src={restaurant.banner_url} alt={restaurant.name} className={`absolute inset-0 w-full h-full object-cover ${closed ? "grayscale" : ""}`} />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-black/10" />
        {closed && (
          <div className="absolute top-4 right-4 z-10 flex items-center gap-2 rounded-full bg-red-600 text-white px-4 py-1.5 text-sm font-semibold shadow-lg" data-testid="storefront-closed-badge">
            <span className="w-2 h-2 rounded-full bg-white" /> Cerrado ahora
          </div>
        )}
        <div className="relative h-full max-w-5xl mx-auto px-6 flex flex-col justify-end pb-8">
          {restaurant.logo_url && (
            <img src={restaurant.logo_url} alt="logo" className="w-16 h-16 rounded-2xl object-cover border-2 border-white/80 mb-4 shadow-lg" />
          )}
          <h1 className="font-heading font-black text-4xl sm:text-5xl text-white tracking-tight" data-testid="storefront-name">{restaurant.name}</h1>
          <p className="text-white/85 mt-2 max-w-xl">{restaurant.description}</p>
          <div className="flex items-center gap-4 mt-3 text-white/70 text-sm">
            {restaurant.address && <span className="flex items-center gap-1"><MapPin className="w-4 h-4" />{restaurant.address}</span>}
            {restaurant.phone && <span className="flex items-center gap-1"><Phone className="w-4 h-4" />{restaurant.phone}</span>}
          </div>
          {hasZones && (
            <p className="text-white/70 text-xs mt-2" data-testid="storefront-zones">Delivery en: {zones.map((z) => z.label).join(" · ")}</p>
          )}
        </div>
      </div>

      {closed && (
        <div className="bg-red-50 border-y border-red-200 text-red-700" data-testid="storefront-closed-banner">
          <div className="max-w-5xl mx-auto px-6 py-3 text-sm font-medium text-center">
            Este restaurante está <strong>cerrado</strong> en este momento. Puedes ver el menú, pero los pedidos no están disponibles hasta que vuelva a abrir.
          </div>
        </div>
      )}

      {/* Sticky category nav */}
      <div className="sticky top-0 z-30 bg-background/80 backdrop-blur-xl border-b border-black/5">
        <div className="max-w-5xl mx-auto px-6 py-3 flex gap-2 overflow-x-auto no-scrollbar">
          {categories.map((c) => (
            <button key={c.id} onClick={() => {
              setActiveCat(c.id);
              document.getElementById(`cat-${c.id}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
              className={`flex items-center gap-2 whitespace-nowrap px-4 py-2 rounded-full text-sm font-medium transition-all ${activeCat === c.id ? "bg-primary text-primary-foreground" : "bg-black/5 text-neutral-600 hover:bg-black/10"}`}
              data-testid={`cat-chip-${c.id}`}>
              {c.image_url && <img src={c.image_url} alt="" className="w-5 h-5 rounded-full object-cover" />}
              {c.name}
            </button>
          ))}
        </div>
      </div>

      {/* Menu */}
      <div className="max-w-5xl mx-auto px-6 py-8 pb-28">
        {categories.map((c) => (
          <section key={c.id} id={`cat-${c.id}`} className="mb-10 scroll-mt-20">
            <h2 className="font-heading font-bold text-2xl mb-5 flex items-center gap-3">
              {c.image_url && <img src={c.image_url} alt="" className="w-9 h-9 rounded-full object-cover" />}
              {c.name}
            </h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {c.items.map((item) => (
                <div key={item.id} className="group flex gap-4 rounded-2xl border border-black/5 bg-white p-3 transition-all hover:-translate-y-1 hover:shadow-lg" data-testid={`menu-item-${item.id}`}>
                  {item.image_url && (
                    <div className="w-24 h-24 rounded-xl overflow-hidden flex-shrink-0">
                      <img src={item.image_url} alt={item.name} className="w-full h-full object-cover transition-transform group-hover:scale-105" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-heading font-semibold text-lg leading-tight">{item.name}</h3>
                    <p className="text-sm text-neutral-500 line-clamp-2 mt-0.5">{item.description}</p>
                    {item.ingredients?.length > 0 && (
                      <p className="text-xs text-neutral-400 mt-1 line-clamp-1" data-testid={`item-ingredients-${item.id}`}>
                        {item.ingredients.join(" · ")}
                      </p>
                    )}
                    <div className="flex items-center justify-between mt-2">
                      <span className="font-bold tabular-nums">{money(item.price)}</span>
                      <Button size="sm" className="h-8 rounded-full" onClick={() => onAdd(item)} disabled={closed} data-testid={`add-btn-${item.id}`}>
                        <Plus className="w-4 h-4 mr-1" /> Añadir
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>

      {/* Floating cart button */}
      {count > 0 && (
        <div className="fixed bottom-5 inset-x-0 z-40 px-6">
          <div className="max-w-5xl mx-auto">
            <Sheet open={cartOpen} onOpenChange={setCartOpen}>
              <SheetTrigger asChild>
                <Button className="w-full h-14 rounded-2xl shadow-2xl text-base gap-3" data-testid="open-cart-btn">
                  <ShoppingBag className="w-5 h-5" />
                  <span className="flex-1 text-left">Ver carrito · {count} {count === 1 ? "artículo" : "artículos"}</span>
                  <span className="tabular-nums">{money(subtotal)}</span>
                </Button>
              </SheetTrigger>
              <SheetContent className="font-body w-full sm:max-w-md flex flex-col" style={themeStyle}>
                <SheetHeader><SheetTitle className="font-heading text-xl">Tu pedido</SheetTitle></SheetHeader>
                <div className="flex-1 overflow-y-auto -mx-6 px-6 py-4 space-y-3">
                  {cart.map((c) => (
                    <div key={c.key} className="flex gap-3 items-center" data-testid={`cart-item-${c.menu_item_id}`}>
                      {c.image && <img src={c.image} alt="" className="w-14 h-14 rounded-lg object-cover" />}
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm truncate">{c.name}</div>
                        {c.modifiers.length > 0 && <div className="text-xs text-neutral-500 truncate">{c.modifiers.map((m) => m.name).join(", ")}</div>}
                        {c.removed && c.removed.length > 0 && <div className="text-xs text-red-500 truncate">{c.removed.map((r) => `Sin ${r}`).join(", ")}</div>}
                        <div className="text-sm tabular-nums">{money(c.unit)}</div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => setQty(c.key, -1)}><Minus className="w-3 h-3" /></Button>
                        <span className="w-6 text-center tabular-nums text-sm">{c.quantity}</span>
                        <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => setQty(c.key, 1)}><Plus className="w-3 h-3" /></Button>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="border-t pt-4 space-y-3">
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1"><Label className="text-xs">Nombre</Label>
                      <Input value={customer.name} onChange={(e) => setCustomer({ ...customer, name: e.target.value })} data-testid="cart-name-input" /></div>
                    <div className="space-y-1"><Label className="text-xs">Teléfono</Label>
                      <Input value={customer.phone} onChange={(e) => setCustomer({ ...customer, phone: e.target.value })} data-testid="cart-phone-input" /></div>
                  </div>
                  <div className="space-y-1"><Label className="text-xs">Dirección de entrega</Label>
                    <Input value={customer.address} placeholder="Calle, número, ciudad, código postal" data-testid="cart-address-input"
                      onChange={(e) => { setCustomer({ ...customer, address: e.target.value }); setZoneError(""); }} />
                    {zoneError && <p className="text-sm text-red-600 font-medium" data-testid="delivery-zone-error">{zoneError}</p>}
                  </div>
                  <div className="space-y-1.5 border-t border-black/5 pt-3">
                    <div className="flex justify-between text-sm text-neutral-500"><span>Subtotal</span><span className="tabular-nums">{money(subtotal)}</span></div>
                    {deliveryFee > 0 && <div className="flex justify-between text-sm text-neutral-500"><span>Envío</span><span className="tabular-nums">{money(deliveryFee)}</span></div>}
                    <div className="flex justify-between font-bold text-lg"><span>Total</span><span className="tabular-nums">{money(grandTotal)}</span></div>
                  </div>
                  <Button className="w-full h-12" onClick={checkout} disabled={paying} data-testid="checkout-btn">
                    {paying ? <Loader2 className="w-4 h-4 animate-spin" /> : `Pagar ${money(grandTotal)}`}
                  </Button>
                  <p className="text-xs text-center text-neutral-400">Pago seguro con Stripe · tarjeta test 4242 4242 4242 4242</p>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      )}

      {/* Modifier dialog */}
      <Dialog open={!!modItem} onOpenChange={(o) => !o && setModItem(null)}>
        <DialogContent className="font-body" style={themeStyle}>
          <DialogHeader><DialogTitle className="font-heading text-xl">{modItem?.name}</DialogTitle>
            <DialogDescription>Personaliza tu plato con las opciones disponibles.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {modItem?.ingredients?.length > 0 && (
              <div>
                <div className="font-medium text-sm mb-2">Ingredientes <span className="text-neutral-400 font-normal">— desmarca los que no quieras</span></div>
                <div className="space-y-2">
                  {modItem.ingredients.map((ing) => (
                    <label key={ing} className="flex items-center gap-3 rounded-lg border p-3 cursor-pointer hover:bg-black/5" data-testid={`ingr-${ing}`}>
                      <Checkbox checked={selIngr[ing] !== false} onCheckedChange={(v) => setSelIngr({ ...selIngr, [ing]: !!v })} />
                      <span className={`flex-1 text-sm ${selIngr[ing] === false ? "line-through text-neutral-400" : ""}`}>{ing}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            {modItem?.modifier_groups?.map((g) => (
              <div key={g.id}>
                <div className="font-medium text-sm mb-2">{g.name}</div>
                <div className="space-y-2">
                  {g.modifiers.map((m) => (
                    <label key={m.id} className="flex items-center gap-3 rounded-lg border p-3 cursor-pointer hover:bg-black/5" data-testid={`mod-${m.id}`}>
                      <Checkbox checked={!!selMods[m.id]} onCheckedChange={(v) => setSelMods({ ...selMods, [m.id]: v })} />
                      <span className="flex-1 text-sm">{m.name}</span>
                      <span className="text-sm tabular-nums text-neutral-500">+{money(m.price_delta)}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button className="w-full" onClick={confirmMods} data-testid="confirm-mods-btn">Añadir al carrito</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <footer className="border-t border-black/5 py-6 text-center text-sm text-neutral-400 flex items-center justify-center gap-2">
        <UtensilsCrossed className="w-4 h-4" /> Powered by Domifood
      </footer>
    </div>
  );
}
